import { CnRequestConfig } from 'cn-request';
import isPlainObject from 'lodash/isPlainObject';
import { CnMessage } from '@cainiaofe/cn-ui';
import qs from 'query-string';
import {
  dataOriginDataEasy,
  dataSourceDataEasy,
  preMokelayDomain,
  formUrlencoded,
  __dataEasyRequestPath__,
  getConeEnv,
  coneEnv,
  coneDomain,
} from '../util/const';
import {
  calculateTextExprValue,
  executeFunction,
  isArrayNotEmpty,
  getRealResponse,
} from '../util/util';
import { BaseRequestConfig } from '@/lowcode/common/types';

const messageTypeMap = {
  success: 'success',
  warning: 'warning',
  error: 'error',
  notice: 'notice',
  help: 'help',
  loading: 'loading',
};

export function getRealData(realRequestConfig, res, _context) {
  let data = res?.data;
  const resultProcessFunc = handleResultProcessFunc(realRequestConfig);
  if (typeof resultProcessFunc === 'function') {
    data = executeFunction(resultProcessFunc, res, _context?.state);
  }
  return data;
}

export function handleResultProcessFunc(requestConfig) {
  if (requestConfig) {
    const { resultProcessFunc, serviceType, mockData } = requestConfig;
    if (typeof resultProcessFunc === 'function') {
      return (res, state) => {
        let newRes = getRealResponse(res);
        try {
          const temp = resultProcessFunc(newRes, state);
          if (temp && temp.data && temp.success !== undefined) {
            newRes = temp.data;
          } else {
            newRes = temp;
          }
        } catch (e) {
          console.error('请求结果回调执行失败', e);
        }
        return newRes;
      };
    } else if (serviceType === 'mock' && isPlainObject(mockData)) {
      return () => {
        return mockData?.data;
      };
    } else {
      return (res) => {
        if (res?.data) {
          return res.data;
        }
        return res;
      };
    }
  }
}

export function isDataEasyDataSource(serviceType?: string, realUrl?: string) {
  return (
    serviceType === dataOriginDataEasy ||
    serviceType === dataSourceDataEasy ||
    realUrl?.includes(__dataEasyRequestPath__)
  );
}

export function getDataEasyUrl(relativeUrl?: string, isDesign = false) {
  if (isDesign && relativeUrl?.startsWith('/')) {
    const env = getConeEnv() as coneEnv;
    const domainUrl = coneDomain[env];
    return `${domainUrl}${relativeUrl}`;
  }
  return relativeUrl;
}

// 获取研发态的requestConfig
export function getDesignRequestConfig(requestConfig?: BaseRequestConfig) {
  const { __serviceType__, __datasourceName__, guid, serviceCode, url, serviceType } =
    requestConfig || {};

  const designRequestUrl = isDataEasyDataSource(serviceType, url) ? getDataEasyUrl(url, true) : url;

  // 如果是数据源模式，则从window上获取
  if (__serviceType__ === 'datasource' && __datasourceName__) {
    const dataSourceRequestConfig =
      window?.top?.CnLowcodeEditor?.mapDatasourceItemToRemoteConfig?.(__datasourceName__);
    if (dataSourceRequestConfig) {
      return {
        ...requestConfig,
        ...dataSourceRequestConfig,
        serviceCode: dataSourceRequestConfig.serviceCode || dataSourceRequestConfig.guid,
        url: designRequestUrl,
      };
    }
  }
  return (
    {
      ...requestConfig,
      serviceCode: serviceCode || guid,
      url: designRequestUrl,
    } || {}
  );
}

// 处理设计态的mokelay请求url
export const handleDesignMokelayUrl = (url) => {
  const domain = window?.getCnLowcodeStoreItem?.('__next_cone_domain__');
  if (domain && domain?.value) {
    return `${location.protocol}//${domain.value}${url}`;
  }
  return preMokelayDomain + url;
};

export function handleRequestParams(params, config = {}) {
  let result = {};
  if (params && params.length > 0) {
    params.forEach((item) => {
      if (item) {
        const { label, value } = item;
        if (label && value !== undefined) {
          result[label] = calculateTextExprValue(value, config);
        }
      }
    });
  } else if (typeof params === 'function') {
    const temp = calculateTextExprValue(params, config);
    if (isPlainObject(temp)) {
      result = temp;
    }
  }
  return result;
}

export function mixHeaders(requestConfig: BaseRequestConfig, state): Record<string, any> {
  const { __extraConfig__ = {}, serviceType, url = '' } = requestConfig;
  const { headers = {} } = __extraConfig__;
  let mixedHeaders = headers;

  if (typeof headers === 'function') {
    mixedHeaders = executeFunction(headers, state);
  }

  // 为DataEasy数据源增加X-Os-Request-App-Code请求头，将接入OS应用的DataEasy请求转发到cnarch-dataeasy；用户设置的headers将覆盖默认headers
  if (isDataEasyDataSource(serviceType, url)) {
    mixedHeaders = {
      'X-Os-Request-App-Code': 'cnarch-dataeasy',
      ...mixedHeaders,
    };
  }

  return mixedHeaders;
}

export function transRequestConfigToRemote(config) {
  const {
    requestConfig,
    handleParams,
    isDesign,
    urlParamsDataSource,
    recordDataSource,
    state,
    extraParamList,
    getExtraParam,
    needFormatResult,
    handleParamsBeforeSend,
  } = config || {};
  if (requestConfig) {
    const realUrl = getRealRequestUrl({
      requestConfig,
      state,
      extraParam: getExtraParam?.(),
    });
    const { url, params = [], serviceType, method, contentType, timeout } = requestConfig;

    const temp: CnRequestConfig = {
      url,
      method: 'post',
      data: {},
    };
    if (realUrl) {
      temp.url = realUrl;
    }

    temp.headers = mixHeaders(requestConfig, state);

    if (timeout) {
      temp.timeout = timeout;
    }

    if (isDesign && serviceType === 'mokelay') {
      temp.url = handleDesignMokelayUrl(temp.url);
    }
    if (serviceType === 'http') {
      temp.withCredentials = true;
    }
    if (typeof handleParams === 'function') {
      temp.data = handleParams(temp.data);
    }

    const realParams = handleRequestParams(params, {
      urlParamsDataSource,
      recordDataSource,
      state,
      extraParamList,
      getExtraParam,
    });
    if (Object.keys(realParams).length > 0) {
      temp.data = { ...temp.data, ...realParams };
    }

    if (serviceType === 'http') {
      if (method) {
        temp.method = method;
      }
      if (contentType === formUrlencoded) {
        temp.headers = {
          'content-type': contentType,
        };
        if (temp.method === 'post' && isPlainObject(temp.data)) {
          temp.data = qs.stringify(temp.data);
        }
      }
    }

    if (typeof handleParamsBeforeSend === 'function') {
      temp.data = handleParamsBeforeSend(temp.data);
    }

    if (temp.method === 'get') {
      temp.params = temp.data;
      delete temp.data;
    }
    if (needFormatResult === true) {
      temp.formatResult = handleResultProcessFunc(requestConfig);
    }
    return temp;
  } else {
    return {};
  }
}

export function handleSuccessMessage(res) {
  const { msg, extra } = res || {};
  const { type } = extra || {};
  let resultType = 'success';
  if (messageTypeMap[type]) {
    resultType = type;
  }
  CnMessage[resultType](msg || '请求成功');
}

export function getRealRequestUrl(config) {
  const { requestConfig, state, extraParam } = config || {};
  let result;
  if (requestConfig) {
    const { dynamicUrl, useDynamicUrl, url } = requestConfig;
    result = url;
    if (useDynamicUrl === true && typeof dynamicUrl === 'function') {
      const temp = executeFunction(dynamicUrl, state, extraParam || {});
      if (typeof temp === 'string') {
        result = temp;
      } else {
        result = '';
      }
    }
  }
  return result;
}

export function handleResultProcessFunc2(response, requestConfig, extra) {
  const { state } = extra || {};
  let newRes = getRealResponse(response);
  if (requestConfig) {
    const { resultProcessFunc, serviceType, mockData } = requestConfig;
    if (
      typeof resultProcessFunc === 'function' &&
      (serviceType === 'mokelay' || serviceType === 'http')
    ) {
      try {
        if (isPlainObject(state)) {
          newRes = resultProcessFunc(newRes, state);
        } else {
          newRes = resultProcessFunc(newRes);
        }
      } catch (e) {
        console.error('请求结果回调执行失败', e);
      }
      return newRes;
    } else if (serviceType === 'mock' && isPlainObject(mockData)) {
      return mockData;
    }
  }
  return newRes;
}

const transProxyToObject = (origin) => {
  const result = {};
  if (typeof origin === 'object') {
    const keys = Object.keys(origin);
    if (keys && keys.length > 0) {
      keys.forEach((key) => {
        result[key] = origin[key];
      });
    }
  }
  return result;
};

export function getFilterValue(config) {
  const { filterConfig, _context } = config || {};
  const { _bindFilter } = filterConfig || {};
  let result = {};
  if (isArrayNotEmpty(_bindFilter)) {
    for (const item of _bindFilter) {
      if (item) {
        const filterDom = _context?.$(item);
        const { _dataSourceName } = filterDom?.props || {};
        if (_dataSourceName) {
          result = {
            ...result,
            ...transProxyToObject(_context?.state?.[_dataSourceName]),
          };
        }
      }
    }
  }
  return result;
}
